import React, { useState, useEffect, useCallback } from 'react';
import {
  HiDocumentText,
  HiPlus,
  HiTrash,
  HiEye,
  HiPrinter,
  HiCheck,
  HiClock,
  HiX,
  HiMail,
  HiCurrencyEuro,
  HiChevronDown,
  HiChevronUp,
  HiFilter,
  HiSearch,
} from 'react-icons/hi';

// Interfaces
interface InvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

interface Invoice {
  id: number;
  invoiceNumber: string;
  clientName: string;
  clientEmail: string;
  clientAddress: string;
  clientPhone: string;
  items: InvoiceItem[];
  subtotal: number;
  taxRate: number;
  total: number;
  status: 'pending' | 'sent' | 'paid' | 'cancelled';
  notes: string;
  createdAt: string;
  paidAt: string | null;
}

interface Client {
  id: number;
  name: string;
  email: string;
  address: string;
  phone: string;
}

interface NewInvoice {
  clientName: string;
  clientEmail: string;
  clientAddress: string;
  clientPhone: string;
  items: InvoiceItem[];
  taxRate: number;
  notes: string;
}

interface Stats {
  total: number;
  pending: number;
  sent: number;
  paid: number;
  cancelled: number;
  totalAmount: number;
  paidAmount: number;
}

const Invoice: React.FC = () => {
  // États
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedInvoice, setExpandedInvoice] = useState<number | null>(null);
  const [stats, setStats] = useState<Stats>({
    total: 0,
    pending: 0,
    sent: 0,
    paid: 0,
    cancelled: 0,
    totalAmount: 0,
    paidAmount: 0,
  });

  const [newInvoice, setNewInvoice] = useState<NewInvoice>({
    clientName: '',
    clientEmail: '',
    clientAddress: '',
    clientPhone: '',
    items: [{ description: '', quantity: 1, unitPrice: 0, total: 0 }],
    taxRate: 0,
    notes: '',
  });

  // Fetch functions
  const fetchInvoices = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACK_URL}/invoice`);
      const data = await response.json();
      setInvoices(data);
    } catch (error) {
      console.error('Erreur lors du chargement des factures:', error);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACK_URL}/client`);
      const data = await response.json();
      setClients(data);
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACK_URL}/invoice/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchInvoices(), fetchClients(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchInvoices, fetchClients, fetchStats]);

  // Handlers
  const handleClientSelect = (clientId: string) => {
    const client = clients.find((c) => c.id === parseInt(clientId));
    if (client) {
      setNewInvoice({
        ...newInvoice,
        clientName: client.name,
        clientEmail: client.email || '',
        clientAddress: client.address || '',
        clientPhone: client.phone || '',
      });
    }
  };

  const addItem = () => {
    setNewInvoice({
      ...newInvoice,
      items: [...newInvoice.items, { description: '', quantity: 1, unitPrice: 0, total: 0 }],
    });
  };

  const removeItem = (index: number) => {
    const items = newInvoice.items.filter((_, i) => i !== index);
    setNewInvoice({ ...newInvoice, items });
  };

  const updateItem = (index: number, field: keyof InvoiceItem, value: string | number) => {
    const items = [...newInvoice.items];
    items[index] = { ...items[index], [field]: value };
    items[index].total = items[index].quantity * items[index].unitPrice;
    setNewInvoice({ ...newInvoice, items });
  };

  const calculateSubtotal = () => {
    return newInvoice.items.reduce((sum, item) => sum + item.total, 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal();
  };

  const createInvoice = async () => {
    try {
      const invoiceData = {
        ...newInvoice,
        subtotal: calculateSubtotal(),
        taxRate: 0,
        total: calculateTotal(),
      };

      const response = await fetch(`${process.env.REACT_APP_BACK_URL}/invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(invoiceData),
      });

      if (response.ok) {
        await Promise.all([fetchInvoices(), fetchStats()]);
        setShowCreateModal(false);
        resetForm();
      }
    } catch (error) {
      console.error('Erreur lors de la création de la facture:', error);
    }
  };

  const updateStatus = async (id: number, status: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACK_URL}/invoice/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });

      if (response.ok) {
        await Promise.all([fetchInvoices(), fetchStats()]);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
    }
  };

  const deleteInvoice = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette facture ?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACK_URL}/invoice/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        await Promise.all([fetchInvoices(), fetchStats()]);
      }
    } catch (error) {
      console.error('Erreur lors de la suppression de la facture:', error);
    }
  };

  const resetForm = () => {
    setNewInvoice({
      clientName: '',
      clientEmail: '',
      clientAddress: '',
      clientPhone: '',
      items: [{ description: '', quantity: 1, unitPrice: 0, total: 0 }],
      taxRate: 0,
      notes: '',
    });
  };

  // Utility functions
  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; icon: React.ReactNode; label: string }> = {
      pending: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-700',
        icon: <HiClock className="w-3 h-3 sm:w-4 sm:h-4" />,
        label: 'En attente',
      },
      sent: {
        bg: 'bg-blue-100',
        text: 'text-blue-700',
        icon: <HiMail className="w-3 h-3 sm:w-4 sm:h-4" />,
        label: 'Envoyée',
      },
      paid: {
        bg: 'bg-green-100',
        text: 'text-green-700',
        icon: <HiCheck className="w-3 h-3 sm:w-4 sm:h-4" />,
        label: 'Payée',
      },
      cancelled: {
        bg: 'bg-red-100',
        text: 'text-red-700',
        icon: <HiX className="w-3 h-3 sm:w-4 sm:h-4" />,
        label: 'Annulée',
      },
    };

    const badge = badges[status] || badges.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.icon}
        <span className="hidden sm:inline">{badge.label}</span>
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const printInvoice = (invoice: Invoice) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const isPaid = invoice.status === 'paid';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Facture ${invoice.invoiceNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #333; background: #fff; }
          .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
          .logo { font-size: 24px; font-weight: bold }
          .invoice-info { text-align: right; }
          .invoice-number { font-size: 20px; font-weight: bold }
          .addresses { display: flex; justify-content: space-between; margin-bottom: 40px; gap: 40px; }
          .address-block { flex: 1; }
          .address-title { font-weight: bold; margin-bottom: 10px; color: #666; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          th { background: #666; color: white; padding: 12px; text-align: left; }
          .items-table{ background: #666; }
          td { padding: 12px; border-bottom: 1px solid #eee; }
          .totals { display: flex; justify-content: flex-end; }
          .totals-table { width: 300px; }
          .totals-table td { padding: 8px 12px; }
          .totals-table .total-row { font-size: 18px; font-weight: bold; background: #f8f8f8; }
          .notes { margin-top: 30px; padding: 20px; background: #f8f8f8; border-radius: 8px; }
          .footer { margin-top: 40px; text-align: center; color: #666; font-size: 12px; }
          .exoneration {padding: 10px; background: #fef3c7; border-radius: 4px; font-size: 11px;text-align: right; }
          .paid-stamp {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-30deg);
            font-size: 72px;
            font-weight: bold;
            color: rgba(34, 197, 94, 0.25);
            border: 8px solid rgba(34, 197, 94, 0.25);
            padding: 20px 40px;
            border-radius: 16px;
            text-transform: uppercase;
            pointer-events: none;
            z-index: 1000;
          }
          @media print {
            body { padding: 20px; }
            .paid-stamp { position: fixed; }
          }
        </style>
      </head>
      <body>
        ${isPaid ? '<div class="paid-stamp">PAYÉ</div>' : ''}
        <div class="header">
          <div>
            <div class="logo">Amicale des Sapeurs-Pompiers</div>
            <div>d'Ozoir-La-Ferrière</div>
          </div>
          <div class="invoice-info">
            <div class="invoice-number">${invoice.invoiceNumber}</div>
            <div>Date: ${formatDate(invoice.createdAt)}</div>
          </div>
        </div>
        
        <div class="addresses">
          <div class="address-block">
            <div class="address-title">ÉMETTEUR</div>
            <div>Amicale des Sapeurs-Pompiers d'Ozoir-La-Ferrière</div>
            <div>amicaleozoir@gmail.com</div>
            <div>5 allee de la breche aux loups</div>
            <div>77330 Ozoir-La-Ferrière</div>
          </div>
          <div class="address-block" style="text-align: right;">
            <div class="address-title">CLIENT</div>
            <div><strong>${invoice.clientName}</strong></div>
            <div>${invoice.clientAddress || ''}</div>
            <div>${invoice.clientPhone || ''}</div>
            <div>${invoice.clientEmail || ''}</div>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th class="items-table">Description</th>
              <th class="items-table" style="text-align: center;">Quantité</th>
              <th class="items-table" style="text-align: right;">Prix unitaire</th>
              <th class="items-table" style="text-align: right;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${invoice.items.map((item: InvoiceItem) => `
              <tr>
                <td>${item.description}</td>
                <td style="text-align: center;">${item.quantity}</td>
                <td style="text-align: right;">${formatCurrency(item.unitPrice)}</td>
                <td style="text-align: right;">${formatCurrency(item.total)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="totals">
          <table class="totals-table">
            <tr>
              <td>Sous-total</td>
              <td style="text-align: right;">${formatCurrency(invoice.subtotal)}</td>
            </tr>
            <tr class="total-row">
              <td>Total</td>
              <td style="text-align: right;">${formatCurrency(invoice.total)}</td>
            </tr>
          </table>
        </div>
        
        ${invoice.notes ? `<div class="notes"><strong>Notes:</strong> ${invoice.notes}</div>` : ''}
        
        <div class="exoneration">
          TVA non applicable - Article 261 B du CGI
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  // Filter invoices
  const filteredInvoices = invoices
    .filter((inv) => filter === 'all' || inv.status === filter)
    .filter(
      (inv) =>
        inv.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.clientName.toLowerCase().includes(searchTerm.toLowerCase())
    );

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Chargement des factures...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-3 sm:p-6 lg:p-8 bg-white">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-3">
              <HiDocumentText className="text-red-600" />
              Factures
            </h1>
            <p className="text-slate-500 text-sm mt-1">Gestion des factures clients</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-4 py-2.5 sm:px-6 sm:py-3 rounded-xl font-medium transition-all shadow-lg hover:shadow-red-500/25"
          >
            <HiPlus className="text-lg" />
            <span>Nouvelle facture</span>
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <HiDocumentText className="text-slate-600 text-lg sm:text-xl" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total</p>
                <p className="text-lg sm:text-2xl font-bold text-slate-800">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-3 sm:p-4 border border-yellow-300 shadow-sm">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <HiClock className="text-yellow-600 text-lg sm:text-xl" />
              </div>
              <div>
                <p className="text-xs text-slate-500">En attente</p>
                <p className="text-lg sm:text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-3 sm:p-4 border border-blue-300 shadow-sm">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <HiMail className="text-blue-600 text-lg sm:text-xl" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Envoyées</p>
                <p className="text-lg sm:text-2xl font-bold text-blue-600">{stats.sent}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-3 sm:p-4 border border-green-300 shadow-sm">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <HiCheck className="text-green-600 text-lg sm:text-xl" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Payées</p>
                <p className="text-lg sm:text-2xl font-bold text-green-600">{stats.paid}</p>
              </div>
            </div>
          </div>

          <div className="col-span-2 sm:col-span-3 lg:col-span-1 bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-3 sm:p-4 border border-green-300 shadow-sm">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-200 rounded-lg flex items-center justify-center">
                <HiCurrencyEuro className="text-green-700 text-lg sm:text-xl" />
              </div>
              <div>
                <p className="text-xs text-green-700">Montant payé</p>
                <p className="text-lg sm:text-2xl font-bold text-green-700">{formatCurrency(stats.paidAmount)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="bg-white rounded-xl p-3 sm:p-4 border border-slate-200 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher une facture..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-slate-50 border border-slate-300 rounded-lg pl-10 pr-4 py-2.5 text-slate-800 placeholder-slate-400 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
              />
            </div>

            {/* Filter buttons */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
              <HiFilter className="text-slate-400 flex-shrink-0" />
              {[
                { value: 'all', label: 'Toutes', color: 'bg-slate-600' },
                { value: 'pending', label: 'En attente', color: 'bg-yellow-500' },
                { value: 'sent', label: 'Envoyées', color: 'bg-blue-500' },
                { value: 'paid', label: 'Payées', color: 'bg-green-500' },
                { value: 'cancelled', label: 'Annulées', color: 'bg-red-500' },
              ].map((f) => (
                <button
                  key={f.value}
                  onClick={() => setFilter(f.value)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                    filter === f.value
                      ? `${f.color} text-white shadow-sm`
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Invoices List */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {filteredInvoices.length === 0 ? (
            <div className="p-8 sm:p-12 text-center">
              <HiDocumentText className="w-12 h-12 sm:w-16 sm:h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-sm sm:text-base">Aucune facture trouvée</p>
            </div>
          ) : (
            <>
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-200 border-b border-slate-300">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">N° Facture</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Client</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Montant</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Statut</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredInvoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-4">
                          <span className="font-mono font-semibold text-red-600">{invoice.invoiceNumber}</span>
                        </td>
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-medium text-slate-800">{invoice.clientName}</p>
                            <p className="text-sm text-slate-500">{invoice.clientEmail}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-slate-600">{formatDate(invoice.createdAt)}</td>
                        <td className="px-4 py-4">
                          <span className="font-semibold text-slate-800">{formatCurrency(invoice.total)}</span>
                        </td>
                        <td className="px-4 py-4">{getStatusBadge(invoice.status)}</td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => {
                                setSelectedInvoice(invoice);
                                setShowViewModal(true);
                              }}
                              className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-600 hover:text-slate-800 transition-colors"
                              title="Voir"
                            >
                              <HiEye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => printInvoice(invoice)}
                              className="p-2 bg-blue-100 hover:bg-blue-200 rounded-lg text-blue-600 transition-colors"
                              title="Imprimer"
                            >
                              <HiPrinter className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteInvoice(invoice.id)}
                              className="p-2 bg-red-100 hover:bg-red-200 rounded-lg text-red-600 transition-colors"
                              title="Supprimer"
                            >
                              <HiTrash className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden divide-y divide-slate-100">
                {filteredInvoices.map((invoice) => (
                  <div key={invoice.id} className="p-4">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setExpandedInvoice(expandedInvoice === invoice.id ? null : invoice.id)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono font-semibold text-red-600 text-sm">{invoice.invoiceNumber}</span>
                          {getStatusBadge(invoice.status)}
                        </div>
                        <p className="font-medium text-slate-800 truncate">{invoice.clientName}</p>
                        <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                          <span>{formatDate(invoice.createdAt)}</span>
                          <span className="font-semibold text-slate-800">{formatCurrency(invoice.total)}</span>
                        </div>
                      </div>
                      <div className="ml-4">
                        {expandedInvoice === invoice.id ? (
                          <HiChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <HiChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </div>

                    {/* Expanded content */}
                    {expandedInvoice === invoice.id && (
                      <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500">Email</p>
                            <p className="text-slate-800 truncate">{invoice.clientEmail || '-'}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Téléphone</p>
                            <p className="text-slate-800">{invoice.clientPhone || '-'}</p>
                          </div>
                        </div>

                        <div>
                          <p className="text-slate-500 text-sm mb-2">Articles ({invoice.items.length})</p>
                          <div className="space-y-1">
                            {invoice.items.slice(0, 3).map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span className="text-slate-600 truncate flex-1 mr-2">{item.description}</span>
                                <span className="text-slate-800 flex-shrink-0">{formatCurrency(item.total)}</span>
                              </div>
                            ))}
                            {invoice.items.length > 3 && (
                              <p className="text-xs text-slate-400">+{invoice.items.length - 3} autres articles</p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 pt-2">
                          <button
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              setShowViewModal(true);
                            }}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-600 hover:text-slate-800 transition-colors text-sm"
                          >
                            <HiEye className="w-4 h-4" />
                            Voir
                          </button>
                          <button
                            onClick={() => printInvoice(invoice)}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-100 hover:bg-blue-200 rounded-lg text-blue-600 transition-colors text-sm"
                          >
                            <HiPrinter className="w-4 h-4" />
                            Imprimer
                          </button>
                          <button
                            onClick={() => deleteInvoice(invoice.id)}
                            className="p-2 bg-red-100 hover:bg-red-200 rounded-lg text-red-600 transition-colors"
                          >
                            <HiTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl my-8 border border-slate-200 shadow-2xl">
            <div className="sticky top-0 bg-white px-4 sm:px-6 py-4 border-b border-slate-200 rounded-t-2xl z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-lg sm:text-xl font-bold text-slate-800">Nouvelle facture</h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <HiX className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Client Info Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nom du client *</label>
                  <input
                    type="text"
                    value={newInvoice.clientName}
                    onChange={(e) => setNewInvoice({ ...newInvoice, clientName: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-slate-800 focus:outline-none focus:border-red-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={newInvoice.clientEmail}
                    onChange={(e) => setNewInvoice({ ...newInvoice, clientEmail: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-slate-800 focus:outline-none focus:border-red-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Adresse</label>
                  <input
                    type="text"
                    value={newInvoice.clientAddress}
                    onChange={(e) => setNewInvoice({ ...newInvoice, clientAddress: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-slate-800 focus:outline-none focus:border-red-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Téléphone</label>
                  <input
                    type="tel"
                    value={newInvoice.clientPhone}
                    onChange={(e) => setNewInvoice({ ...newInvoice, clientPhone: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-slate-800 focus:outline-none focus:border-red-500"
                  />
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-slate-700">Articles *</label>
                  <button
                    onClick={addItem}
                    className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
                  >
                    <HiPlus className="w-4 h-4" />
                    Ajouter
                  </button>
                </div>

                <div className="space-y-3">
                  {newInvoice.items.map((item, index) => (
                    <div key={index} className="bg-slate-50 rounded-lg p-3 sm:p-4 border border-slate-200">
                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                        <div className="sm:col-span-5">
                          <label className="block text-xs text-slate-500 mb-1 sm:hidden">Description</label>
                          <input
                            type="text"
                            placeholder="Description"
                            value={item.description}
                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                            className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-slate-800 text-sm focus:outline-none focus:border-red-500"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-xs text-slate-500 mb-1 sm:hidden">Quantité</label>
                          <input
                            type="number"
                            placeholder="Qté"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                            className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-slate-800 text-sm focus:outline-none focus:border-red-500"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-xs text-slate-500 mb-1 sm:hidden">Prix unitaire</label>
                          <input
                            type="number"
                            placeholder="Prix €"
                            min="0"
                            step="0.01"
                            value={item.unitPrice || ''}
                            onChange={(e) => updateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                            className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-slate-800 text-sm focus:outline-none focus:border-red-500"
                          />
                        </div>
                        <div className="sm:col-span-2 flex items-end">
                          <div className="w-full bg-slate-100 rounded-lg px-3 py-2 text-slate-800 text-sm font-medium text-right">
                            {formatCurrency(item.total)}
                          </div>
                        </div>
                        <div className="sm:col-span-1 flex items-end justify-center">
                          {newInvoice.items.length > 1 && (
                            <button
                              onClick={() => removeItem(index)}
                              className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <HiTrash className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes</label>
                <textarea
                  value={newInvoice.notes}
                  onChange={(e) => setNewInvoice({ ...newInvoice, notes: e.target.value })}
                  rows={3}
                  className="w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 text-slate-800 focus:outline-none focus:border-red-500 resize-none"
                  placeholder="Notes ou informations complémentaires..."
                />
              </div>

              {/* Total */}
              <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                <div className="flex justify-between items-center text-lg">
                  <span className="text-slate-700">Total</span>
                  <span className="text-2xl font-bold text-red-600">{formatCurrency(calculateTotal())}</span>
                </div>
                <p className="text-xs text-slate-500 mt-2">TVA non applicable - Article 261 B du CGI</p>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white px-4 sm:px-6 py-4 border-t border-slate-200 rounded-b-2xl">
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 font-medium transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={createInvoice}
                  disabled={!newInvoice.clientName || newInvoice.items.every((i) => !i.description)}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:from-slate-400 disabled:to-slate-500 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-all"
                >
                  Créer la facture
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8 border border-slate-200 shadow-2xl">
            <div className="sticky top-0 bg-white px-4 sm:px-6 py-4 border-b border-slate-200 rounded-t-2xl z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-slate-800 font-mono">{selectedInvoice.invoiceNumber}</h2>
                  <p className="text-sm text-slate-500">{formatDate(selectedInvoice.createdAt)}</p>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(selectedInvoice.status)}
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <HiX className="w-5 h-5 text-slate-500" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-6 max-h-[60vh] overflow-y-auto">
              {/* Client Info */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <h3 className="text-sm font-medium text-slate-500 mb-3">Client</h3>
                <div className="space-y-2">
                  <p className="text-lg font-semibold text-slate-800">{selectedInvoice.clientName}</p>
                  {selectedInvoice.clientAddress && <p className="text-slate-600">{selectedInvoice.clientAddress}</p>}
                  <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                    {selectedInvoice.clientEmail && <span>{selectedInvoice.clientEmail}</span>}
                    {selectedInvoice.clientPhone && <span>{selectedInvoice.clientPhone}</span>}
                  </div>
                </div>
              </div>

              {/* Items */}
              <div>
                <h3 className="text-sm font-medium text-slate-500 mb-3">Articles</h3>
                <div className="space-y-2">
                  {selectedInvoice.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-3 border-b border-slate-100 last:border-0">
                      <div className="flex-1 min-w-0 mr-4">
                        <p className="text-slate-800">{item.description}</p>
                        <p className="text-sm text-slate-500">
                          {item.quantity} × {formatCurrency(item.unitPrice)}
                        </p>
                      </div>
                      <span className="font-medium text-slate-800 flex-shrink-0">{formatCurrency(item.total)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Total</span>
                  <span className="text-2xl font-bold text-red-600">{formatCurrency(selectedInvoice.total)}</span>
                </div>
              </div>

              {/* Notes */}
              {selectedInvoice.notes && (
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <h3 className="text-sm font-medium text-slate-500 mb-2">Notes</h3>
                  <p className="text-slate-600">{selectedInvoice.notes}</p>
                </div>
              )}

              {/* Status Update */}
              <div>
                <h3 className="text-sm font-medium text-slate-500 mb-3">Changer le statut</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { value: 'pending', label: 'En attente', bg: 'bg-yellow-100', activeBg: 'bg-yellow-500', text: 'text-yellow-700' },
                    { value: 'sent', label: 'Envoyée', bg: 'bg-blue-100', activeBg: 'bg-blue-500', text: 'text-blue-700' },
                    { value: 'paid', label: 'Payée', bg: 'bg-green-100', activeBg: 'bg-green-500', text: 'text-green-700' },
                    { value: 'cancelled', label: 'Annulée', bg: 'bg-red-100', activeBg: 'bg-red-500', text: 'text-red-700' },
                  ].map((s) => (
                    <button
                      key={s.value}
                      onClick={() => {
                        updateStatus(selectedInvoice.id, s.value);
                        setSelectedInvoice({ ...selectedInvoice, status: s.value as Invoice['status'] });
                      }}
                      disabled={selectedInvoice.status === s.value}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        selectedInvoice.status === s.value
                          ? `${s.activeBg} text-white`
                          : `${s.bg} ${s.text} hover:opacity-80`
                      } disabled:opacity-50`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white px-4 sm:px-6 py-4 border-t border-slate-200 rounded-b-2xl">
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 font-medium transition-colors"
                >
                  Fermer
                </button>
                <button
                  onClick={() => printInvoice(selectedInvoice)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium transition-colors"
                >
                  <HiPrinter className="w-5 h-5" />
                  Imprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoice;
